<template>
  <b-modal ref="all-communications-modal-ref" hide-footer hide-header>
    <div class="vtd-modal-content">
      <div class="vtd-modal-header mt-4">
        <h5 class="vtd-modal-title">All Communications</h5>
        <button @click="closeModal" type="button" class="close" aria-label="Close">
          <span aria-hidden="true"><i class="material-icons">&#xE5CD;</i></span>
        </button>
      </div>

      <div class="vtd-modal-body">
        <b-alert v-if="replySuccess" show variant="success" class="text-center">
          Your reply has been sent.
        </b-alert>

        <div v-if="etrailItems.length" class="d-flex justify-content-end mb-2">
          <b-link @click="toggleAll" class="cursor">
            {{ expandAll ? 'Collapse All' : 'Expand All' }}
          </b-link>
        </div>

        <div v-for="(etrail, index) in etrailItems" :key="etrail._id" class="etrail-accordion-item">
          <button
            type="button"
            class="etrail-accordion-header"
            :class="{ 'item-active': etrail.expand, 'etrail-accordion-header-unread': hasUnreadMessages(etrail) }"
            :aria-controls="`etrail-collapse-${etrail._id}`"
            :aria-expanded="etrail.expand"
            @click="toggleExpand(index)"
          >
            <span class="etrail-accordion-field">{{ formatDate(etrail.submitted) }}</span>
            <span class="etrail-accordion-field">{{ etrail.passenger && etrail.passenger.surname }}</span>
            <span class="etrail-accordion-field">
              {{ etrail.category }}
              <span v-if="hasUnreadMessages(etrail)" class="etrail-unread-dot" title="Unread message" />
            </span>
            <span class="etrail-accordion-toggle">{{ etrail.expand ? '-' : '+' }}</span>
          </button>
          <b-collapse :id="`etrail-collapse-${etrail._id}`" v-model="etrail.expand">
            <div class="etrail-accordion-body">
              <div
                v-for="message in getMessages(etrail)"
                :key="message._id"
                class="etrail-message-item"
                :class="message.client ? 'etrail-message-item-client' : 'etrail-message-item-staff'"
                v-visible="{ etrailId: etrail._id, message }"
              >
                <div class="etrail-message-row-wrapper">
                  <div
                    class="etrail-message-bubble"
                    :class="[message.client ? 'etrail-message-row-client' : 'etrail-message-row-staff', { 'etrail-message-unread': isUnread(message) }]"
                  >
                    <div class="etrail-message-content" v-html="message.message"></div>

                    <div v-if="message.clientResponseAllowed === 'yes'" class="etrail-reply">
                      <button
                        v-if="activeReplyMessageId !== message._id"
                        type="button"
                        class="etrail-reply-btn"
                        @click="openReply(message._id)"
                      >
                        <i class="material-icons etrail-reply-icon">&#xE15E;</i>
                        Reply to this message
                      </button>

                      <div v-else class="etrail-reply-form">
                        <b-form-textarea
                          v-model="replyText"
                          rows="3"
                          max-rows="6"
                          placeholder="Type your reply..."
                        />
                        <small v-if="replyError" class="d-block text-danger mt-1">
                          Sorry, we were unable to send your reply. Please try again.
                        </small>
                        <div class="d-flex justify-content-end mt-2">
                          <b-button variant="link" :disabled="replySubmitting" @click="cancelReply" class="mr-2">Cancel</b-button>
                          <b-button
                            variant="primary"
                            :disabled="!replyText.trim() || replySubmitting"
                            @click="submitReply(etrail, message)"
                          >
                            <b-spinner v-if="replySubmitting" small class="mr-1" />
                            Submit
                          </b-button>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div class="etrail-message-side-meta">
                    <div class="etrail-message-meta">
                      <span v-if="message.client">{{ titleCase(message.sender) }} &middot; </span>{{ formatDateTime(message.sentAt) }} GMT
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </b-collapse>
        </div>
      </div>
    </div>
  </b-modal>
</template>

<script>
import { mapGetters, mapActions } from "vuex";
import { COMMUNICATIONS_PRIVACY_ENABLED } from "@/lib/feature-flags";

export default {
  directives: {
    visible: {
      inserted(el, binding, vnode) {
        const { etrailId, message } = binding.value || {}
        if (!message || message.client || message.read) return
        const observer = new IntersectionObserver((entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              vnode.context.markMessageAsRead(etrailId, message._id)
              observer.disconnect()
            }
          })
        }, { threshold: 0.5 })
        observer.observe(el)
        el.__unreadObserver = observer
      },
      unbind(el) {
        if (el.__unreadObserver) {
          el.__unreadObserver.disconnect()
        }
      },
    },
  },
  data(){
    return {
      etrailItems: [],
      expandAll: false,
      activeReplyMessageId: null,
      replyText: '',
      replySubmitting: false,
      replyError: false,
      replySuccess: false,
    }
  },
  computed: {
    ...mapGetters({
      completedEtrails: "communications/getCompletedEtrails",
      booking: "booking/getBooking",
      paxNames: "booking/getBookingPassengers",
      verifiedPassengerId: "communications/getVerifiedPassengerId",
    }),
  },
  watch: {
    completedEtrails: {
      immediate: true,
      handler(etrails) {
        const previousExpandById = this.etrailItems.reduce((acc, item) => {
          acc[item._id] = item.expand
          return acc
        }, {})
        this.etrailItems = (etrails || [])
          .filter(this.canViewEtrail)
          .map((etrail) => ({
            ...etrail,
            expand: previousExpandById[etrail._id] || false,
          })).reverse()
      },
    },
  },
  methods: {
    ...mapActions({
      sendClientResponseToEtrailAction: 'communications/sendClientResponseToEtrailAction',
      markMessagesAsReadAction: 'communications/markMessagesAsReadAction',
    }),
    // On a multi-passenger booking, an etrail is only visible to the
    // verified passenger unless the submitter explicitly checked "allow
    // other guests to view" on the form. Single-passenger bookings have no
    // privacy concern, and the feature flag is a full bypass for testing.
    canViewEtrail(etrail) {
      if (!COMMUNICATIONS_PRIVACY_ENABLED) return true
      if (!this.paxNames || this.paxNames.length <= 1) return true
      if (etrail.shareWithOtherPassengers) return true
      return !!(
        etrail.passenger && etrail.passenger.id === this.verifiedPassengerId
      )
    },
    isUnread(message) {
      return !message.client && !message.read
    },
    hasUnreadMessages(etrail) {
      return this.getMessages(etrail).some(this.isUnread)
    },
    markMessageAsRead(etrailId, messageId) {
      this.markMessagesAsReadAction({ etrailId, messageIds: [messageId] })
    },
    async showModal(flightno) {
      this.$refs["all-communications-modal-ref"].show();
    },
    closeModal() {
      this.$refs["all-communications-modal-ref"].hide();
    },
    toggleExpand(index) {
      this.etrailItems[index].expand = !this.etrailItems[index].expand
    },
    toggleAll() {
      this.expandAll = !this.expandAll
      this.etrailItems = this.etrailItems.map((etrail) => ({
        ...etrail,
        expand: this.expandAll,
      }))
    },
    formatDate(value) {
      if (!value) return ''
      const date = new Date(value)
      if (isNaN(date.getTime())) return value
      return date.toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' })
    },
    formatDateTime(value) {
      if (!value) return ''
      const date = new Date(value)
      if (isNaN(date.getTime())) return value
      return date.toLocaleString('en-GB', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      })
    },
    titleCase(str) {
      if (!str) return ''
      return str
        .split(' ')
        .map((word) => word.slice(0, 1).toUpperCase() + word.slice(1).toLowerCase())
        .join(' ')
    },
    getMessages(etrail) {
      if (etrail.messages && etrail.messages.length) {
        return [...etrail.messages].sort((a, b) => new Date(a.sentAt) - new Date(b.sentAt))
      }
      if (!etrail.message) return []
      return [{
        _id: etrail._id,
        message: etrail.message,
        sender: `${(etrail.passenger && etrail.passenger.firstname) || ''} ${(etrail.passenger && etrail.passenger.surname) || ''}`.trim(),
        sentAt: etrail.submitted,
        client: true,
      }]
    },
    openReply(messageId) {
      this.activeReplyMessageId = messageId
      this.replyText = ''
      this.replyError = false
    },
    cancelReply() {
      this.activeReplyMessageId = null
      this.replyText = ''
      this.replyError = false
    },
    async submitReply(etrail, message) {
      if (!this.replyText.trim()) return
      this.replySubmitting = true
      this.replyError = false
      const res = await this.sendClientResponseToEtrailAction({
        etrailId: etrail._id,
        payload: {
          messageId: message._id,
          message: this.replyText.trim(),
          sourceId: 'APP',
        },
        bookno: this.booking.user_information.bookingNumber,
      })
      this.replySubmitting = false
      if (res && res.error) {
        this.replyError = true
        return
      }
      this.cancelReply()
      this.replySuccess = true
      setTimeout(() => {
        this.replySuccess = false
        this.closeModal()
      }, 3000)
    },
  },
};
</script>
<style lang="scss">
@import "./styles.scss";
</style>
